import { ArrowUpRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ContactCTA from "@/components/site/ContactCTA";
import { useReveal } from "@/hooks/useReveal";
import { useNavHandler } from "@/hooks/useNavHandler";
import { MEDIA_SUPPORTS } from "@/lib/constants";

const MediaHero = () => {
  const nav = useNavHandler();
  return (
    <section
      id="top"
      data-testid="media-hero"
      className="relative min-h-[86vh] flex items-center pt-32 pb-24 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-px w-full bg-border/70" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-10">
              <span className="h-px w-12 bg-accent" />
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.32em] text-accent font-semibold">
                Duneworth · Media
              </span>
            </div>
            <h1
              data-testid="media-headline"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl text-primary leading-[1.04] tracking-tight text-balance"
            >
              Media, content, and marketing{" "}
              <em className="italic">built around software growth.</em>
            </h1>
            <p
              data-testid="media-sub"
              className="mt-10 text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl text-pretty"
            >
              Duneworth is first and foremost a software company. Duneworth
              Media is the internal operation that helps our platforms — and
              occasionally aligned outside partners — communicate with clarity
              and grow with intent.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5">
              <a
                href="#media-supports"
                onClick={nav("#media-supports")}
                data-testid="media-cta-primary"
                className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/85 px-8 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors duration-300"
              >
                What We Support
              </a>
              <a
                href="/#contact"
                onClick={nav("/#contact")}
                data-testid="media-cta-secondary"
                className="inline-flex items-center justify-center bg-transparent text-primary border border-primary hover:bg-primary hover:text-primary-foreground px-8 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors duration-300"
              >
                Start a Conversation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MediaIntro = () => (
  <section
    data-testid="media-intro"
    className="py-24 sm:py-32 border-t border-border"
  >
    <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-10 bg-accent" />
            <span className="text-[11px] uppercase tracking-[0.32em] text-accent font-semibold">
              Software First
            </span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-primary leading-[1.05] tracking-tight text-balance">
            Everything else <em className="italic">in service of it.</em>
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 flex flex-col gap-6">
          <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
            Duneworth builds and operates modern digital platforms designed to
            solve specific problems, create better user experiences, and support
            scalable business operations.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
            But great software does not succeed through product alone. The way a
            product is positioned, explained, photographed, filmed, launched,
            and marketed directly shapes how customers understand it, trust it,
            and adopt it.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
            That is why Duneworth has naturally developed a small internal
            operation around content, brand, and marketing execution —{" "}
            <span className="text-primary font-serif italic">Duneworth Media</span>.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const MediaBuilt = () => {
  const { ref, shown } = useReveal();
  const lines = [
    "A product photo is not just a product photo.",
    "A launch email is not just an email.",
    "A short-form video is not just content.",
  ];
  return (
    <section
      data-testid="media-built"
      className="relative bg-primary text-primary-foreground py-24 sm:py-32 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-accent/30" />
        <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-accent/5 blur-3xl" />
      </div>

      <div
        ref={ref}
        className={`relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 reveal ${
          shown ? "in" : ""
        }`}
      >
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-10 bg-accent" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-accent font-semibold">
                Product · Brand · Execution
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-balance">
              Built from the <em className="italic">intersection</em>.
            </h2>
          </div>
          <div className="lg:col-span-7 flex flex-col gap-6">
            <p className="text-lg leading-relaxed text-primary-foreground/85 text-pretty">
              Duneworth Media is not a traditional agency. It is an operating
              function that grew out of building real products.
            </p>
            <p className="text-base leading-relaxed text-primary-foreground/65 text-pretty">
              Our approach is grounded in the realities of software, customer
              acquisition, product-market fit, conversion, and business growth.
              We think beyond the asset itself and focus on how each piece of
              content supports a larger outcome.
            </p>

            <div className="mt-6 space-y-4">
              {lines.map((l) => (
                <div
                  key={l}
                  className="border-t border-primary-foreground/20 pt-4 font-serif italic text-xl sm:text-2xl leading-snug"
                >
                  {l}
                </div>
              ))}
              <div className="border-t border-primary-foreground/20 pt-6">
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Each one is part of the larger system that helps a customer
                  understand the product, believe in the brand, and take action.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SupportCard = ({ index, title, body, delay = 0 }) => {
  const { ref, shown } = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`media-support-${index}`}
      className={`group relative border border-border bg-card p-8 sm:p-10 flex flex-col h-full transition-colors duration-500 hover:border-accent reveal ${
        shown ? "in" : ""
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between mb-8">
        <span className="text-[11px] uppercase tracking-[0.32em] text-accent font-semibold">
          0{index}
        </span>
        <ArrowUpRight
          size={18}
          className="text-primary/40 group-hover:text-accent transition-all duration-500"
        />
      </div>
      <h3 className="font-serif text-2xl sm:text-3xl text-primary leading-tight mb-5 text-balance">
        {title}
      </h3>
      <p className="text-base text-muted-foreground leading-relaxed text-pretty">
        {body}
      </p>
      <div className="mt-8 h-px w-12 bg-accent/70 group-hover:w-24 transition-all duration-500" />
    </article>
  );
};

const MediaSupports = () => (
  <section
    id="media-supports"
    data-testid="media-supports"
    className="py-24 sm:py-32 border-t border-border"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 mb-16 sm:mb-20">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-10 bg-accent" />
            <span className="text-[11px] uppercase tracking-[0.32em] text-accent font-semibold">
              What Duneworth Media Supports
            </span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance">
            Where software, brand, <br className="hidden sm:block" />
            and marketing <em className="italic">meet</em>.
          </h2>
        </div>
        <div className="lg:col-span-5 lg:col-start-8 flex items-end">
          <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
            A practical scope of work across the five areas where Duneworth
            Media creates the most leverage — internally and, when aligned,
            externally.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
        {MEDIA_SUPPORTS.map((s, i) => (
          <SupportCard key={s.title} index={i + 1} {...s} delay={i * 80} />
        ))}
      </div>
    </div>
  </section>
);

const MediaOutside = () => {
  const nav = useNavHandler();
  return (
    <section
      data-testid="media-outside"
      className="py-24 sm:py-32 border-t border-border bg-card"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-10 bg-accent" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-accent font-semibold">
                Select Outside Work
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-primary leading-[1.05] tracking-tight text-balance">
              Occasional, <em className="italic">when aligned.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex flex-col gap-6">
            <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
              Duneworth Media primarily supports our own software platforms. We
              occasionally take on outside projects when there is strong
              alignment — typically with brands, founders, and operators who
              need more than basic content creation.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
              The ideal fit is a business that needs help translating its
              product, service, or brand into clear, compelling marketing
              assets that can be used across digital channels.
            </p>

            <div className="pt-6 border-t border-border">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-accent font-semibold mb-2">
                    Best Fit
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Thoughtful positioning, high-quality creative, practical
                    understanding of how content supports growth.
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-accent font-semibold mb-2">
                    Not A Fit
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Volume content, one-off asset production, projects that
                    treat marketing as separate from product.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6">
              <a
                href="/#contact"
                onClick={nav("/#contact")}
                data-testid="media-outside-cta"
                className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/85 px-8 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors duration-300"
              >
                Discuss a Project
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MediaClosing = () => (
  <section
    data-testid="media-closing"
    className="py-24 sm:py-32 border-t border-border"
  >
    <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
      <div className="flex items-center justify-center gap-4 mb-8">
        <span className="h-px w-10 bg-accent" />
        <span className="text-[11px] uppercase tracking-[0.32em] text-accent font-semibold">
          A Strategic Extension
        </span>
        <span className="h-px w-10 bg-accent" />
      </div>
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-primary leading-[1.15] tracking-tight text-balance">
        Software companies need more than code to win.
      </h2>
      <p className="mt-10 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto text-pretty">
        They need clear positioning, strong storytelling, useful content, and
        consistent execution. Duneworth Media exists to support that layer of
        the business — helping our platforms, and occasionally aligned outside
        partners, communicate more clearly and grow more effectively.
      </p>
      <div className="mt-14 inline-flex items-center gap-6">
        <span className="h-px w-12 bg-accent" />
        <p className="font-serif italic text-2xl text-primary">
          Product · Brand · Execution.
        </p>
        <span className="h-px w-12 bg-accent" />
      </div>
    </div>
  </section>
);

export default function MediaPage() {
  return (
    <div data-testid="duneworth-media" className="bg-background text-foreground">
      <Header />
      <main>
        <MediaHero />
        <MediaIntro />
        <MediaBuilt />
        <MediaSupports />
        <MediaOutside />
        <MediaClosing />
        <ContactCTA />
      </main>
      <Footer />
    </div>
  );
}
